import { esc } from "../ui/format.js";
import { uiGet, uiHeader, uiKpis, uiMoney } from "../ui/kit.js";
import { renderDelegationInto, renderGoalsInto, renderInboxInto } from "./ceo.js";
import { renderDynastyInto } from "./dynasty.js";
import { renderMomentsInto } from "./moments.js";
import { renderWrappedInto } from "./wrapped.js";
import { renderWealthInto } from "./wealth.js";
import { renderCareerInto } from "./career.js";
import { renderTimeMachineInto } from "./timemachine.js";
import { EM_HOME } from "./empire.js";

/**
 * The CEO Office: everything that needs the CEO personally, in one place.
 *
 *   Inbox       decisions your departments bring you (ceo.js)
 *   Agenda      invitations to speak, interviews and summits (agenda.js)
 *   Goals       the goal ladder
 *   Delegation  how much each department runs itself
 *   Moments     your industry's Signature Moment (moments.js); opens first while one is on
 *   Dynasty     the CEO lineage and its inherited traits (dynasty.js)
 *   Wrapped     each year's recap (wrapped.js)
 *   Wealth      the founder's own money: shares, loans, things, a foundation (wealth.js)
 *   Career      exits and the next company, the serial entrepreneur (career.js)
 *   Time        the timeline and what-if branches (timemachine.js)
 *
 * plus a strip saying what's waiting in each, and
 * every letter waiting in the other systems (workers, climate, takeovers...).
 * The last sub-tab is remembered per browser.
 */
const OFFICE_KEY = "ges.office.tab";
const OFFICE_TABS = [["moments", "⭐ Moments"], ["inbox", "📥 Inbox"], ["agenda", "🎙 Agenda"], ["goals", "🏁 Goals"], ["delegation", "🧭 Delegation"],
  ["dynasty", "👑 Dynasty"], ["wrapped", "🎁 Wrapped"], ["wealth", "💰 Wealth"], ["career", "🧳 Career"], ["timemachine", "⏳ Time Machine"]];

function officeTab() {
  try { const t = localStorage.getItem(OFFICE_KEY); return OFFICE_TABS.some(([k]) => k === t) ? t : "inbox"; } catch (e) { return "inbox"; }
}

/**
 * Your office, from a garage to a tower (backend/office_building.py; Rachit 2026-10-22).
 * 2D: each stage is its own drawing; lit windows follow how many people work there.
 */
function ofBuilding(b) {
  if (!b || b.people == null || !Array.isArray(b.stages)) return "";
  const W = 640, H = 210, G = 190;
  const lit = Math.max(0.25, Math.min(1, b.next ? b.people / Math.max(1, b.next.people) : 1));
  let seed = b.people * 7 + 3;
  const rnd = () => { seed = (seed * 1103515245 + 12345) >>> 0; return (seed >>> 8) / 16777216; };
  const block = (x, w, floors, fh = 9, cols = 0, cls = "of-b") => {
    const h = floors * fh + 8, y = G - h;
    const c = cols || Math.max(2, Math.floor(w / 14));
    let win = "";
    for (let f = 0; f < floors; f++) for (let k = 0; k < c; k++) {
      const wx = x + 6 + k * ((w - 12) / c), wy = y + 6 + f * fh;
      win += `<rect x="${wx.toFixed(1)}" y="${wy.toFixed(1)}" width="${Math.max(3, (w - 12) / c - 4).toFixed(1)}" height="${fh - 4}" rx="1" class="${rnd() < lit ? "of-win on" : "of-win"}"/>`;
    }
    return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" class="${cls}"/>${win}`;
  };
  const art = [
    // garage
    () => `<path d="M250,${G} L250,130 L320,92 L390,130 L390,${G} Z" class="of-b"/><rect x="270" y="140" width="100" height="50" rx="2" class="of-door"/>
      ${[0, 1, 2, 3].map((i) => `<line x1="272" x2="368" y1="${150 + i * 11}" y2="${150 + i * 11}" class="of-slat"/>`).join("")}
      <rect x="296" y="112" width="48" height="14" rx="2" class="of-win on"/>`,
    () => block(240, 160, 2, 22, 5, "of-b of-brick") + `<rect x="300" y="170" width="40" height="20" class="of-door"/>`,
    () => block(210, 220, 4, 18, 8),
    () => block(220, 200, 8, 15, 7) + `<rect x="270" y="${G - 8 * 15 - 24}" width="100" height="14" rx="3" class="of-sign"/><text x="320" y="${G - 8 * 15 - 13}" text-anchor="middle" class="of-sign-t">${esc(b.company.slice(0, 14))}</text>`,
    () => block(120, 140, 6, 14) + block(270, 120, 10, 14) + block(400, 130, 7, 14) + `<rect x="100" y="${G}" width="450" height="4" class="of-ground"/>`,
    () => `${block(150, 70, 6, 10)}${block(440, 80, 8, 10)}${block(270, 110, 17, 10, 6, "of-b of-tower")}
      <path d="M270,${G - 178} L325,${G - 200} L380,${G - 178} Z" class="of-b of-tower"/><rect x="290" y="${G - 175}" width="70" height="10" rx="2" class="of-sign"/>`,
  ][b.stage] || (() => "");
  const next = b.next ? `Next: <b>${esc(b.next.name)}</b> at ${uiMoney(b.next.valuation, { compact: true })} valuation (${b.next.valuation_pct}%) and ${b.next.people.toLocaleString("en-US")} people (${b.next.people_pct}%)` : "You've built the tallest thing on the skyline.";
  return `<section class="of-building" aria-label="Your office: ${esc(b.name)}">
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(b.name)}"><rect x="0" y="${G}" width="${W}" height="${H - G}" class="of-ground"/>${art()}</svg>
    <div class="of-building-t"><span class="nm-kicker">Your office · ${b.people.toLocaleString("en-US")} people</span><h3>${esc(b.name)}</h3><p>${esc(b.what)}</p>
      <div class="of-stages">${b.stages.map((s, i) => `<span class="${i <= b.stage ? "on" : ""}" title="${esc(s.name)}: ${uiMoney(s.valuation, { compact: true })} and ${s.people} people">${esc(s.name)}</span>`).join("")}</div>
      <p class="sx-card-sub">${next}</p></div>
  </section>`;
}

async function renderOffice(main, snap) {
  // everything the office shows, asked for at once
  let [box, agenda, goals, mom, letters, building] = await Promise.all([uiGet("/inbox").catch(() => ({ open: [] })), uiGet("/ceo-agenda").catch(() => null),
    uiGet("/goals").catch(() => null), uiGet("/moments").catch(() => null), uiGet("/decisions").then((d) => d.open || []).catch(() => []),
    uiGet("/office-building").catch(() => null)]);
  const waiting = (box.open || []).length;
  if (mom && !mom.moment) mom = null; // no answer: no Moments strip
  const live = !!(mom && mom.current);
  // a live Signature Moment opens first, once; after that the remembered sub-tab wins
  let tab = officeTab();
  if (live) {
    try { if (localStorage.getItem("ges.office.moment") !== String(mom.current.id)) { tab = "moments"; localStorage.setItem("ges.office.moment", String(mom.current.id)); } } catch (e) { tab = "moments"; }
  }
  const counts = { inbox: waiting, agenda: agenda ? (agenda.invitations || []).length : 0, moments: live ? 1 : 0,
    wealth: letters.filter((d) => d.system === "founder").length };
  main.innerHTML = `
    <div class="sx of">
      ${uiHeader(`CEO Office — Month ${snap.month}`, "Your own desk: the decisions your departments bring you, the stages you're invited onto, the goals you're climbing and who runs what while you're busy.")}
      ${uiKpis([
        { label: "Decisions waiting", value: `${waiting}`, sub: waiting ? "your departments need you" : "inbox clear" },
        { label: "Invitations", value: agenda ? `${(agenda.invitations || []).length}` : "—", sub: agenda ? `${agenda.slots_left} of ${agenda.max_per_month} calendar slots free` : "" },
        { label: "Promises open", value: agenda ? `${agenda.totals.open}` : "—", sub: agenda ? `${agenda.totals.kept} kept · ${agenda.totals.broken} broken` : "" },
        { label: "Goals", value: goals ? `${goals.completed} of ${goals.total}` : "—", sub: "achieved" },
        { label: mom ? mom.moment.title : "Signature Moment", value: live ? "It's on" : mom ? `Month ${mom.next_month}` : "—", sub: live ? "make your call" : "next one" },
      ])}
      ${building ? ofBuilding(building) : ""}
      ${letters.length ? `<div class="of-letters">${letters.map((d) => {
        const [tab, label] = EM_HOME[d.system] || ["office", d.system];
        return `<button type="button" class="of-letter" data-of-go="${tab}" data-of-sys="${esc(d.system)}"><b>${esc(label)}</b><span>${esc(d.title)}</span><u>Answer →</u></button>`;
      }).join("")}</div>` : ""}
      <nav class="of-tabs" role="tablist">${OFFICE_TABS.map(([k, label]) => `<button type="button" role="tab" class="of-tab${k === tab ? " on" : ""}" data-of-tab="${k}" aria-selected="${k === tab}">${label}${counts[k] ? `<i>${counts[k]}</i>` : ""}</button>`).join("")}</nav>
      <div class="of-body" id="of-body"></div>
    </div>`;
  const body = main.querySelector("#of-body");
  const show = async (k) => {
    try { localStorage.setItem(OFFICE_KEY, k); } catch (e) { /* not remembered */ }
    main.querySelectorAll("[data-of-tab]").forEach((b) => { b.classList.toggle("on", b.dataset.ofTab === k); b.setAttribute("aria-selected", String(b.dataset.ofTab === k)); });
    body.innerHTML = "";
    const host = document.createElement("section");
    host.className = { inbox: "ceo-inbox", goals: "ceo-goals", delegation: "delegation-panel", agenda: "of-agenda", moments: "of-moments",
      dynasty: "of-dynasty", wrapped: "of-wrapped", wealth: "of-wealth em", career: "of-career em", timemachine: "of-tm em" }[k];
    if (k === "inbox") host.id = "ceo-inbox";
    body.appendChild(host);
    if (k === "moments") await renderMomentsInto(host);
    else if (k === "dynasty") await renderDynastyInto(host);
    else if (k === "wrapped") await renderWrappedInto(host);
    else if (k === "wealth") await renderWealthInto(host);
    else if (k === "career") await renderCareerInto(host);
    else if (k === "timemachine") await renderTimeMachineInto(host);
    else if (k === "inbox") await renderInboxInto(host);
    else if (k === "goals") await renderGoalsInto(host);
    else if (k === "delegation") await renderDelegationInto(host);
    else if (k === "agenda") await window.renderAgenda(host, snap);
  };
  main.querySelectorAll("[data-of-tab]").forEach((b) => b.addEventListener("click", () => show(b.dataset.ofTab)));
  main.querySelectorAll("[data-of-go]").forEach((b) => b.addEventListener("click", () => {
    if (b.dataset.ofSys === "founder") return show("wealth");
    return window.ges.showTab(b.dataset.ofGo);
  }));
  await show(tab);
}

window.renderOffice = renderOffice;

export { renderOffice };
